import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { Adapt } from './lib/adapt.js';
import { SceneClock } from './lib/scene-clock.js';

// Contrat du rig partagé : interdit. Le ciel est un seul shader.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'eclipse',
  family: 'cosmic',
  danceability: 0.10,
  energyLevel: 0.05,
  bpmMin: 40,
  bpmMax: 110,
  movement: 0.05,
  tonality: 0.95
};

// ECLIPSE : une éclipse totale de soleil en accéléré. La lune grignote le
// dernier croissant, l'anneau de diamant éclate, les grains de Baily
// s'éteignent un à un, la couronne se déploie en filaments nacrés, les
// étoiles et deux planètes apparaissent, l'horizon s'embrase tout autour ;
// puis tout s'inverse et la boucle reprend. Ambient, néo-classique, musique
// de film, piano, méditation. Règles de kiosk-app/CLAUDE.md (R1 à R19).
//
// LE TEMPS DE SCÈNE (« R20 » des briefs, non écrite dans CLAUDE.md) : le
// module partagé lib/scene-clock.js, période 24 s, départ en phase 0 (en
// plein dans l'action : un croissant déjà très fin), indépendant de la
// musique, gelé en pause. ?eclipset=<s> force le temps de scène (le
// ?scenet=<s> global du module aussi).
//   0-3 s    le croissant s'amincit, la lumière baisse
//   3-4 s    ANNEAU DE DIAMANT (étoile à 6 branches au-dessus du seuil de
//            bloom), puis 5 GRAINS DE BAILY qui s'éteignent un à un
//   4-18 s   TOTALITÉ : couronne, étoiles, planètes, protubérances, horizon
//   18-19 s  anneau de diamant de l'autre côté
//   19-24 s  le croissant revient de l'autre côté, puis la lune glisse
//            autour du soleil (le croissant fait le tour du limbe) jusqu'au
//            croissant fin du départ : on ne repasse jamais par la totalité.
//
// UN QUAD PLEIN ÉCRAN. Le soleil et la lune au centre de composition (R19),
// 22 % de la hauteur ; avec la pochette AU CENTRE, ils s'agrandissent juste
// assez pour que la pochette carrée s'inscrive dans le disque de la lune
// (elle « se pose sur la lune ») - sinon le bloc de la pochette, 72 % de la
// hauteur, cachait l'anneau de diamant, les grains et les protubérances.
//
// LA COURONNE, en coordonnées polaires autour du soleil : des centaines de
// filaments radiaux (bruit 1D périodique en angle, trois échelles, élevé au
// cube pour les affiner, à peine courbé avec le rayon), jusqu'à 3 rayons, en
// loi de puissance plus douce à l'équateur (les « streamers » longs) qu'aux
// pôles ; aux pôles, des plumes qui suivent des lignes de champ d'un dipôle
// (elles s'écartent en montant). Nacrée, blanc bleuté, un coeur lumineux
// au bord de la lune. Le bruit dérive très lentement ; la couronne se
// déploie à l'entrée de la totalité et se rétracte à la sortie.
// adapt.intensity (lissée, aucune impulsion, R9) allonge les filaments et
// avive les protubérances.
//
// L'HORIZON : des collines en silhouette noire jusqu'au bas du cadre (R13),
// leur crête éclairée par la lueur orangée qui embrase tout l'horizon
// pendant la totalité (un coucher de soleil à 360°).

const CYCLE = 24;
const R_BASE = 0.22;                    // 22 % of the screen height (height units: the screen is 2 tall)
const MOON_K = 1.025;                   // the moon is a little larger than the sun: a real totality

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function sm(e0, e1, x) { const k = clamp01((x - e0) / (e1 - e0)); return k * k * (3 - 2 * k); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}

// THE SCRIPT: the moon's offset (units of the sun's radius) and the phase
// lights, from the scene time (0..24)
export function script(t) {
  t = ((t % CYCLE) + CYCLE) % CYCLE;
  const A = 0.085;                                         // the very thin crescent of the start
  let mx, my;
  if (t < 4) { const u = sm(0, 4, t); mx = A * (1 - u) + 0.012 * u; my = 0.004; }       // slides in, side A
  else if (t < 18) { const u = (t - 4) / 14; mx = 0.012 - 0.024 * u; my = 0.004; }      // totality: |m| < 0.025
  else if (t < 21.5) { const u = sm(18, 21.5, t); mx = -0.012 - (A - 0.012) * u; my = 0.004; }  // out, side B
  else { const u = sm(21.5, 24, t), a = Math.PI * (1 - u); mx = A * Math.cos(a); my = A * Math.sin(a) * 0.9 + 0.004 * u; }   // round the limb back to side A
  // the totality's lights: corona deploy, stars, the horizon fire
  const total = sm(3.8, 6.0, t) * (1 - sm(16.0, 18.2, t));
  // the diamond ring (3-3.6 s side A, 18-18.6 s side B) and Baily's beads (3.5-4.0 / 18.5-19.0)
  const ringA = t >= 3 && t < 3.75 ? Math.sin(Math.PI * (t - 3) / 0.75) : 0;
  const ringB = t >= 18.15 && t < 18.9 ? Math.sin(Math.PI * (t - 18.15) / 0.75) : 0;
  const beadsA = t >= 3.45 && t < 4.1 ? (t - 3.45) / 0.65 : -1;           // 0..1, the beads go out one by one
  const beadsB = t >= 17.95 && t < 18.5 ? 1 - (t - 17.95) / 0.55 : -1;     // they come back on the other side
  return { t, mx, my, total, ringA, ringB, beadsA, beadsB };
}

// ----------------------------------------------------------------- the shader

const VERTEX = `
  varying vec2 vUv;
  void main() { vUv = position.xy * 0.5 + 0.5; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;
const FRAGMENT = `
  precision highp float;
  uniform vec2 uCenter;          // the composition centre (height units)
  uniform float uAspect;
  uniform float uPx;             // height units per pixel
  uniform float uR;              // the sun's radius
  uniform vec2 uMoon;            // the moon's offset, units of R
  uniform float uTotal;          // 0..1 the totality's lights
  uniform float uRingA;          // the diamond ring, side A / side B
  uniform float uRingB;
  uniform float uBeadsA;         // < 0 none; 0..1 going out
  uniform float uBeadsB;
  uniform float uDrift;          // the corona's slow drift
  uniform float uLen;            // corona length factor (adapt.intensity)
  uniform float uProm;           // prominences brightness (adapt.intensity)
  uniform float uSafeAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv;
  const float PI = 3.14159265;
  float h11(float n) { return fract(sin(mod(n, 289.0) * 12.9898) * 43758.5453); }
  float h21(vec2 p) { return fract(sin(mod(dot(p, vec2(12.9898, 78.233)), 289.0)) * 43758.5453); }
  // periodic 1D value noise: period P cells
  float vn(float x, float P) {
    float i = floor(x), f = x - i; f = f * f * (3.0 - 2.0 * f);
    return mix(h11(mod(i, P)), h11(mod(i + 1.0, P)), f);
  }
  float vn2(vec2 p) {
    vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
    return mix(mix(h21(i), h21(i + vec2(1.0, 0.0)), f.x), mix(h21(i + vec2(0.0, 1.0)), h21(i + vec2(1.0)), f.x), f.y);
  }
  float fbm(vec2 p) { return 0.55 * vn2(p) + 0.3 * vn2(p * 2.1) + 0.15 * vn2(p * 4.3); }
  float safeAtt(vec2 s) {
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(0.5, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  // the hills: a ridge line, never higher than 22 % of the screen
  float ridge(float x) { return -0.80 + 0.07 * vn(x * 2.2 + 3.0, 997.0) + 0.035 * vn(x * 6.1 + 11.0, 997.0) + 0.012 * vn(x * 19.0, 997.0); }
  // a star with six diffraction spikes (the diamond ring), above the bloom threshold
  vec3 diamond(vec2 p, vec2 c, float k) {
    vec2 d = p - c; float r = length(d) / uPx;
    float core = exp(-r * r / 18.0) * 9.0 + exp(-r / 14.0) * 1.2;
    float spikes = 0.0;
    for (int i = 0; i < 3; i++) {
      float a = float(i) * PI / 3.0 + 0.25;
      vec2 dir = vec2(cos(a), sin(a));
      float along = abs(dot(d, dir)) / uPx, across = abs(dot(d, vec2(-dir.y, dir.x))) / uPx;
      spikes += exp(-across * across / 1.2) * exp(-along / 70.0) * 1.6;
    }
    return vec3(1.0, 0.97, 0.9) * (core + spikes) * k;
  }
  void main() {
    vec2 ndc = vUv * 2.0 - 1.0;
    vec2 sp = vec2(ndc.x * uAspect, ndc.y);
    vec2 p = sp - uCenter;
    float r = length(p), th = atan(p.y, p.x);
    float rr = r / uR;                                            // radius in sun radii
    vec2 mc = uMoon * uR;                                         // the moon's centre
    float dm = length(p - mc) - uR * ${MOON_K.toFixed(3)};          // signed distance to the moon's edge
    float uncovered = clamp((length(uMoon) - 0.025) / 0.12, 0.0, 1.0);   // the light left
    // THE SKY: twilight blue -> deep navy at totality, lighter toward the horizon
    float hz = clamp((sp.y + 0.8) / 1.8, 0.0, 1.0);
    // (already dark: a calm scene keeps its mean luminance under 0.25, R10)
    vec3 skyDay = mix(vec3(0.045, 0.080, 0.155), vec3(0.016, 0.032, 0.080), hz);
    vec3 skyNight = mix(vec3(0.020, 0.024, 0.050), vec3(0.006, 0.008, 0.022), hz);
    vec3 col = mix(skyNight, skyDay, uncovered * 0.75) * (1.0 - 0.4 * uTotal);
    // the stars and two planets, at totality
    if (uTotal > 0.01) {
      vec2 g = floor(sp * 180.0); float hs = h21(g);
      if (hs > 0.9965 && rr > 3.2) { vec2 o = (vec2(h21(g + 7.0), h21(g + 13.0)) - 0.5) * 0.7; float d = length(fract(sp * 180.0) - 0.5 - o) * (1.0 / 180.0) / uPx; col += vec3(0.85, 0.9, 1.0) * (1.0 - smoothstep(0.4, 1.4, d)) * (0.35 + 0.65 * h21(g + 3.0)) * uTotal * 0.8; }
      for (int k = 0; k < 2; k++) {
        vec2 pl = k == 0 ? vec2(-0.95, 0.30) : vec2(1.05, -0.18);
        float d = length(sp - pl) / uPx;
        col += (k == 0 ? vec3(1.0, 0.98, 0.92) : vec3(1.0, 0.92, 0.80)) * (exp(-d * d / 3.0) * 1.1 + exp(-d / 5.0) * 0.12) * uTotal;
      }
    }
    // THE HORIZON FIRE all round: an orange glow over the hills at totality
    float ry = ridge(sp.x);
    float above = sp.y - ry;
    // (measured from a SMOOTH horizon line: following the fine relief, the glow made vertical streaks)
    float hzLine = -0.80 + 0.07 * vn(sp.x * 2.2 + 3.0, 997.0);
    float ah = max(sp.y - hzLine, 0.0);
    col += vec3(1.0, 0.42, 0.12) * exp(-ah / 0.11) * 0.42 * uTotal * step(0.0, above);
    col += vec3(0.9, 0.25, 0.30) * exp(-ah / 0.34) * 0.10 * uTotal * step(0.0, above);
    // THE CORONA, outside the moon
    if (rr > 0.98 && uTotal > 0.001) {
      float u = th / (2.0 * PI) + 0.5;                             // 0..1 round the sun
      float lat = abs(sin(th));                                    // 0 equator (horizontal), 1 poles
      float bend = 0.010 * (rr - 1.0);
      // fine striations: three periodic scales, gently sharpened (hundreds of thin filaments)
      float fil = 0.45 * vn((u + bend) * 120.0 + uDrift * 3.0, 120.0) + 0.33 * vn((u - bend) * 290.0 + uDrift * 7.0, 290.0) + 0.22 * vn(u * 640.0 + uDrift * 13.0, 640.0);
      fil = 0.45 + 1.1 * fil * fil;
      // the HELMET STREAMERS: broad lobes near the equator, long and tapering;
      // the poles stay short (with their plumes below)
      float eq = 1.0 - lat;                                        // 1 on the equator
      float helm = smoothstep(0.35, 0.85, vn(u * 9.0 + uDrift * 0.4, 9.0)) * pow(eq, 1.5);
      float Lmax = (1.6 + (1.5 + 1.4 * helm) * eq * eq) * uLen * (0.55 + 0.45 * uTotal);   // up to ~3 radii on the equator
      float pw = mix(1.7, 5.5, lat * lat) - 0.5 * helm;
      float fall = pow(max(rr, 1.0), -pw) * (1.0 - smoothstep(Lmax * 0.55, Lmax, rr));
      // a streamer narrows as it rises (its lobe tapers): weight the envelope by the lobe
      float c = fall * fil * (0.6 + 1.4 * helm) * mix(1.4, 0.5, lat);
      // polar plumes: along a dipole's field lines, spreading as they rise
      float pole = sp.y > uCenter.y ? 1.0 : -1.0;
      float phi = atan(p.x, pole * p.y);                           // angle from the pole
      float q = phi / (1.0 + 0.55 * (rr - 1.0));
      // (short and faint: strong and long, the plumes took over and the corona was longest at the POLES)
      float plume = pow(vn(q * 60.0 + 40.0 + uDrift, 997.0), 4.0) * smoothstep(0.80, 0.97, lat) * pow(rr, -4.5) * 0.7 * (1.0 - smoothstep(1.5, 1.9, rr));
      // the bright inner corona at the moon's edge
      // (bright, but not so far over the bloom threshold that the bloom greys the moon)
      float inner = exp(-max(dm, 0.0) / (uR * 0.05)) * 1.1 + exp(-max(dm, 0.0) / (uR * 0.22)) * 0.45;
      float cor = (c + plume) * 0.9 + inner;
      // a bigger sun (the cover in the centre) spreads its corona over more of the
      // screen: dimmed in proportion, the calm mean stays under 0.25 (R10)
      col += vec3(0.88, 0.93, 1.05) * cor * uTotal * step(0.0, dm) * pow(${(0.22).toFixed(2)} / uR, 1.3) * 0.85;
    }
    // PROMINENCES: pink flames at the limb, slowly evolving
    if (uTotal > 0.001 && rr > 0.98 && rr < 1.25) {
      float pr = 0.0;
      for (int k = 0; k < 4; k++) {
        float a0 = float(k) * 1.71 + 0.6;
        float da = atan(sin(th - a0), cos(th - a0));
        float w = 0.10 + 0.04 * float(k - 2 * (k / 2));
        // a flame: tallest in its middle, tapering to its sides, licked by fbm
        float prof = 1.0 - abs(da) / w;
        float hgt = (0.10 + 0.12 * fbm(vec2(da * 9.0, uDrift * 0.3 + float(k) * 5.0))) * uR * clamp(prof * 1.6, 0.0, 1.0);
        float shape = fbm(vec2(da * 18.0 + float(k), (r - uR) / (uR * 0.06) - uDrift * 0.6));
        float inside = step(0.0, prof) * (1.0 - smoothstep(hgt * 0.55, hgt, dm)) * smoothstep(0.0, uPx * 1.5, dm);
        pr += inside * smoothstep(0.25, 0.6, shape);
      }
      // MIXED over the corona, not added (pink + the white inner corona read white)
      col = mix(col, vec3(1.2, 0.30, 0.52) * uProm, clamp(pr * 1.4, 0.0, 1.0) * uTotal);
    }
    // THE SUN: the crescent left outside the moon (anti-aliased in pixels), limb-darkened, above the bloom threshold
    float ds = r - uR;
    float sunIn = (1.0 - smoothstep(-0.5, 0.5, ds / uPx)) * smoothstep(-0.5, 0.5, dm / uPx);
    float limb = sqrt(max(1.0 - rr * rr, 0.0));
    col += vec3(1.0, 0.96, 0.86) * sunIn * (1.6 + 1.6 * limb);
    // a thin glow round the crescent (the sky's scattering)
    col += vec3(0.9, 0.8, 0.6) * exp(-max(ds, 0.0) / (uR * 0.12)) * uncovered * 0.25 * step(0.0, dm);
    // THE MOON: black, a faint earthshine
    if (dm < 0.0) col = mix(col, vec3(0.006, 0.007, 0.010), 1.0 - smoothstep(-1.0, 0.0, dm / uPx));
    // THE DIAMOND RING and BAILY'S BEADS on the moon's edge, where the last of the sun is
    vec2 dirA = -normalize(vec2(0.13, 0.004)), dirB = -dirA;
    float edgeR = uR * 1.0;
    if (uRingA > 0.0) col += diamond(p, dirA * edgeR, uRingA);
    if (uRingB > 0.0) col += diamond(p, dirB * edgeR, uRingB);
    for (int k = 0; k < 5; k++) {
      float fk = float(k);
      float a = (fk - 2.0) * 0.16 + 0.03 * sin(fk * 3.7);
      if (uBeadsA >= 0.0) {
        float on = 1.0 - step(fk / 5.0 + 0.08, uBeadsA);          // they go out one by one
        vec2 bp = vec2(cos(atan(dirA.y, dirA.x) + a), sin(atan(dirA.y, dirA.x) + a)) * edgeR;
        float d = length(p - bp) / uPx;
        col += vec3(1.0, 0.97, 0.9) * (exp(-d * d / 4.0) * 3.0 + exp(-d / 6.0) * 0.3) * on;
      }
      if (uBeadsB >= 0.0) {
        float on = 1.0 - step(fk / 5.0 + 0.08, uBeadsB);
        vec2 bp = vec2(cos(atan(dirB.y, dirB.x) + a), sin(atan(dirB.y, dirB.x) + a)) * edgeR;
        float d = length(p - bp) / uPx;
        col += vec3(1.0, 0.97, 0.9) * (exp(-d * d / 4.0) * 3.0 + exp(-d / 6.0) * 0.3) * on;
      }
    }
    // THE HILLS: pure black down to the bottom of the frame (R13), their crest
    // lit 1.5-3 px by the glow behind them, in proportion to its facing (the top)
    if (above < 0.0) {
      float slope = (ridge(sp.x + uPx) - ridge(sp.x - uPx)) / (2.0 * uPx);
      float facing = 1.0 / sqrt(1.0 + slope * slope);
      float dpx = -above / uPx * facing;
      float rim = (1.0 - smoothstep(1.5, 3.0, dpx)) * facing;
      col = vec3(1.0, 0.48, 0.18) * rim * (0.15 + 0.85 * uTotal) * 0.8;
    }
    gl_FragColor = vec4(col * safeAtt(sp), 1.0);
  }
`;

// =============================================================================

export class EclipseVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 2;
    const ft = parseFloat(urlParam('eclipset'));
    this._forcedT = Number.isFinite(ft) ? ft : null;
    // calm: no beat in the post chain, a gentle bloom for the diamond
    this._post = { bloomStrength: 0.55, bloomThreshold: 0.78, grain: 0.012, vignette: 0.35, aberration: 0.0 };
    if (urlParam('eclipseprobe') === '1' && typeof window !== 'undefined') window.__eclipse = this;
  }

  get postProcessing() { return this._post; }

  init(scene, camera) {
    this._scene = scene; this._camera = camera;
    this._prevBackground = scene.background;
    scene.background = new THREE.Color(0x000000);
    this._adapt = new Adapt();
    this._U = {
      uCenter: { value: new THREE.Vector2() }, uAspect: { value: 1.78 }, uPx: { value: 2 / 1080 }, uR: { value: R_BASE },
      uMoon: { value: new THREE.Vector2(0.13, 0) }, uTotal: { value: 0 }, uRingA: { value: 0 }, uRingB: { value: 0 },
      uBeadsA: { value: -1 }, uBeadsB: { value: -1 }, uDrift: { value: 0 }, uLen: { value: 1 }, uProm: { value: 1 },
      uSafeAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._geo = new THREE.PlaneGeometry(2, 2);
    this._mat = new THREE.ShaderMaterial({ uniforms: this._U, vertexShader: VERTEX, fragmentShader: FRAGMENT, depthTest: false, depthWrite: false });
    this._mesh = new THREE.Mesh(this._geo, this._mat);
    this._mesh.frustumCulled = false; this._mesh.renderOrder = -10;
    const self = this;
    this._mesh.onBeforeRender = function (renderer) { renderer.getDrawingBufferSize(_size); self._U.uPx.value = 2 / Math.max(1, _size.y); };
    scene.add(this._mesh);
    this._clock = new SceneClock({ period: CYCLE, start: 0 });
    this._len = 1; this._prom = 1;
    this._push();
  }

  // the sun's radius: 22 % of the height; with the cover IN THE CENTRE, just
  // enough for the square cover to sit inside the moon's disc
  _radius() {
    const sc = safeZoneUniforms.uSafeCenter.value, sh = safeZoneUniforms.uSafeHalfSize.value, st = safeZoneUniforms.uStageCenter.value;
    const centred = sh.x > 0.02 && sh.y > 0.02 && Math.hypot(sc.x - st.x, sc.y - st.y) < 0.15;
    if (!centred) return R_BASE;
    const half = Math.hypot(sh.x + Math.abs(sc.x - st.x), sh.y + Math.abs(sc.y - st.y));
    return Math.max(R_BASE, half / 1.025 * 1.01);
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    const dt = paused ? 0 : Math.min(deltaTime, 0.1);
    const a = this._adapt.update(dt, profile);
    this._clock.update(dt);                                    // the scene's own clock, frozen in pause
    // the music only lengthens the corona and brightens the prominences, slowly (no pulse, R9)
    const k = 1 - Math.exp(-dt / 4);
    this._len += ((0.80 + 0.40 * a.intensity) - this._len) * k;
    this._prom += ((0.65 + 0.55 * a.intensity) - this._prom) * k;
    this._push();
  }

  _push() {
    const U = this._U;
    const s = script(this._forcedT != null ? this._forcedT : this._clock.t);
    this._s = s;
    U.uCenter.value.copy(safeZoneUniforms.uStageCenter.value);
    U.uAspect.value = safeZoneUniforms.uAspect.value || 1.78;
    U.uR.value = this._radius();
    U.uMoon.value.set(s.mx, s.my);
    U.uTotal.value = s.total; U.uRingA.value = s.ringA; U.uRingB.value = s.ringB;
    U.uBeadsA.value = s.beadsA; U.uBeadsB.value = s.beadsB;
    U.uDrift.value = this._clock.elapsed * 0.02;          // the corona drifts across cycles (no jump at the loop)
    U.uLen.value = this._len; U.uProm.value = this._prom;
  }

  getProbe() {
    const s = this._s || {};
    return { sceneTime: +this._clock.elapsed.toFixed(3), t: +(s.t || 0).toFixed(3), moon: [+(s.mx || 0).toFixed(4), +(s.my || 0).toFixed(4)], total: +(s.total || 0).toFixed(3), ringA: +(s.ringA || 0).toFixed(3), ringB: +(s.ringB || 0).toFixed(3), beadsA: s.beadsA, beadsB: s.beadsB, R: +this._U.uR.value.toFixed(3), len: +this._len.toFixed(3) };
  }

  dispose() {
    if (this._scene) { this._scene.remove(this._mesh); this._scene.background = this._prevBackground || null; }
    if (this._geo) this._geo.dispose();
    if (this._mat) this._mat.dispose();
    if (this._adapt) this._adapt.dispose();
    if (typeof window !== 'undefined' && window.__eclipse === this) window.__eclipse = null;
    this._scene = null;
  }
}

const _size = new THREE.Vector2();
